import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/joy';
import Box from '@mui/joy/Box';
import Input from '@mui/joy/Input';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { primaryColor } from '../theme/constants';

function mergeInputValidationHandlers(t, label, slotProps) {
  const baseInput = slotProps?.input || {};

  const handleInvalid = (e) => {
    const input = e.target;
    const fieldLabel = label != null && String(label).trim() !== '' ? label : '';

    if (input.validity.valueMissing) {
      input.setCustomValidity(
        fieldLabel
          ? t('validation.fieldRequired', { field: fieldLabel })
          : t('validation.genericRequired')
      );
    } else if (input.validity.tooShort) {
      input.setCustomValidity(t('validation.tooShort', { min: input.minLength }));
    } else if (input.validity.tooLong) {
      input.setCustomValidity(t('validation.tooLong', { max: input.maxLength }));
    } else if (input.validity.badInput) {
      input.setCustomValidity(t('validation.invalidNumber'));
    } else if (input.validity.rangeUnderflow || input.validity.rangeOverflow) {
      const min = input.getAttribute('min');
      const max = input.getAttribute('max');
      if (min != null && min !== '' && max != null && max !== '') {
        input.setCustomValidity(t('validation.numberBetween', { min, max }));
      } else if (min != null && min !== '') {
        input.setCustomValidity(t('validation.numberMin', { min }));
      } else if (max != null && max !== '') {
        input.setCustomValidity(t('validation.numberMax', { max }));
      } else {
        input.setCustomValidity(t('validation.invalidNumber'));
      }
    } else if (input.validity.stepMismatch) {
      input.setCustomValidity(t('validation.invalidStep'));
    } else {
      input.setCustomValidity(t('validation.invalidValue'));
    }

    if (typeof baseInput.onInvalid === 'function') baseInput.onInvalid(e);
  };

  const handleInput = (e) => {
    e.target.setCustomValidity('');
    if (typeof baseInput.onInput === 'function') baseInput.onInput(e);
  };

  return {
    ...slotProps,
    input: {
      ...baseInput,
      onInvalid: handleInvalid,
      onInput: handleInput,
    },
  };
}

export default function CustomTextField({
  label,
  xs,
  startDecorator,
  endDecorator,
  onChange,
  slotProps,
  value,
  required,
  type,
  disabled,
  display,
  name = '',
  inputRef,
  /** Shows an info icon after the label; full text on hover. */
  hintTooltip,
  /** Short clarification shown under the input with an icon. */
  helperText,
  helperIcon: HelperIcon,
}) {
  const { t } = useTranslation('ui');
  const InlineIcon = HelperIcon || InfoOutlined;

  const mergedSlotProps = useMemo(
    () => mergeInputValidationHandlers(t, label, slotProps),
    [t, label, slotProps]
  );

  return (
    <Grid
      xs={xs}
      display={display}
      sx={{
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(95, 9, 194, 0.3)', // Matches the hover color
          borderRadius: '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
        >
          <span>{required ? `${label} *` : label}</span>
          {hintTooltip ? (
            <Tooltip
              title={hintTooltip}
              variant="soft"
              placement="top"
              sx={{ maxWidth: { xs: 'min(90vw, 420px)', sm: 420 } }}
            >
              <IconButton
                component="span"
                tabIndex={0}
                size="sm"
                variant="plain"
                sx={{ p: 0.25, minHeight: 'auto', verticalAlign: 'middle' }}
                aria-label={t('widgets.fieldHintMoreInfo')}
                onClick={(e) => e.preventDefault()}
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'neutral.500' }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </FormLabel>
      <Input
        inputRef={inputRef}
        disabled={disabled}
        slotProps={mergedSlotProps}
        type={type}
        value={value}
        onChange={onChange}
        name={name}
        startDecorator={startDecorator}
        endDecorator={endDecorator}
        required={required}
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: 'px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid transparent',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: primaryColor,
          },
          '&:focus, &:active': {
            borderColor: primaryColor, // Persistent hover color when clicked
            boxShadow: 'none', // Removes shadow on click
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
        }}
        placeholder={t('widgets.placeholderField', { field: label })}
      />
      {helperText ? (
        <FormHelperText
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            mt: 0.5,
            mx: 0,
          }}
        >
          <InlineIcon
            aria-hidden
            sx={{
              fontSize: 16,
              mt: '1px',
              flexShrink: 0,
              color: 'primary.500',
              opacity: 0.9,
            }}
          />
          <Box component="span" sx={{ fontSize: '12px', color: 'neutral.600', lineHeight: 1.45 }}>
            {helperText}
          </Box>
        </FormHelperText>
      ) : null}
    </Grid>
  );
}
